import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CarrierReviewTagDto, PendingCarrierReviewsDto } from "../../../../dto/Deal/CarrierReviewDto";
import type { DealStatus } from "../../../../dto/Deal/DealDto";

export type ReviewContext = { key: string; dealId: number; companyId: number; userId: number };
export type ReviewSnapshot = {
  dealId: number;
  companyId: number;
  status: DealStatus;
  logisticianId?: number;
  carrierIds: number[];
  special: boolean;
  observer: boolean;
};
export type ReviewCard = {
  carrierId: number;
  name: string;
  rating: number;
  tagIds: number[];
  comment: string;
  status: "draft" | "sending" | "submitted";
  requestId?: string;
  error?: string;
};
export type ReviewRequest = { contextKey: string; requestId: string };
export type SubmitReview = ReviewRequest & { carrierId: number };
export type CarrierReviewsState = {
  context?: ReviewContext;
  snapshot?: ReviewSnapshot;
  open: boolean;
  dismissed: boolean;
  loading: boolean;
  pendingRequestId?: string;
  loadError: boolean;
  notice?: string;
  tags: CarrierReviewTagDto[];
  cards: ReviewCard[];
  submittedIds: number[];
};

export const isReviewStatus = (status?: DealStatus) => status === "CARGO_DELIVERED" || status === "COMPLETED";
const initialState: CarrierReviewsState = {
  open: false,
  dismissed: false,
  loading: false,
  loadError: false,
  tags: [],
  cards: [],
  submittedIds: [],
};

const carrierReviewsSlice = createSlice({
  name: "carrierReviews",
  initialState,
  reducers: {
    enter(_state, action: PayloadAction<ReviewContext>) {
      return { ...initialState, context: action.payload };
    },
    leave(state, action: PayloadAction<string>) {
      if (state.context?.key === action.payload) return initialState;
    },
    observeDeal(state, action: PayloadAction<ReviewSnapshot>) {
      const next = action.payload;
      if (state.context?.dealId !== next.dealId || state.context.companyId !== next.companyId) return;
      const previous = state.snapshot;
      state.pendingRequestId = undefined;
      state.loading = false;
      if (previous && !isReviewStatus(previous.status) && isReviewStatus(next.status)) {
        state.dismissed = false;
      }
      state.snapshot = next;
      if (!isReviewStatus(next.status) || next.logisticianId !== state.context.userId || next.special || next.observer) {
        state.open = false;
        state.cards = [];
        state.notice = undefined;
      }
    },
    dismiss(state) {
      state.open = false;
      state.dismissed = true;
    },
    hideSubmitted(state, action: PayloadAction<number>) {
      state.cards = state.cards.filter((card) => card.carrierId !== action.payload || card.status !== "submitted");
      if (!state.cards.length && !state.notice) state.open = false;
    },
    loadPending(state, action: PayloadAction<ReviewRequest>) {
      if (state.context?.key !== action.payload.contextKey) return;
      state.loading = true;
      state.loadError = false;
      state.pendingRequestId = action.payload.requestId;
    },
    loadPendingSuccess(state, action: PayloadAction<ReviewRequest & { data: PendingCarrierReviewsDto }>) {
      const { contextKey, requestId, data } = action.payload;
      if (state.context?.key !== contextKey || state.pendingRequestId !== requestId) return;
      state.loading = false;
      state.loadError = false;
      if (!data.eligible) {
        state.open = false;
        state.cards = [];
        state.tags = [];
        return;
      }
      state.tags = data.tags;
      const activeTagIds = new Set(data.tags.map((tag) => tag.id));
      const currentIds = new Set(data.carriers.map((carrier) => carrier.carrierId));
      const retained = state.cards.filter((card) => currentIds.has(card.carrierId) || card.status !== "draft");
      state.cards = retained.map((card) => ({
        ...card,
        name: data.carriers.find((carrier) => carrier.carrierId === card.carrierId)?.name ?? card.name,
        tagIds: card.status === "draft" ? card.tagIds.filter((id) => activeTagIds.has(id)) : card.tagIds,
      }));
      for (const carrier of data.carriers) {
        if (!state.cards.some((card) => card.carrierId === carrier.carrierId) && !state.submittedIds.includes(carrier.carrierId)) {
          state.cards.push({ ...carrier, rating: 0, tagIds: [], comment: "", status: "draft" });
        }
      }
      state.open = !state.dismissed && Boolean(state.cards.length || state.notice);
    },
    loadPendingReject(state, action: PayloadAction<ReviewRequest>) {
      if (state.context?.key !== action.payload.contextKey || state.pendingRequestId !== action.payload.requestId) return;
      state.loading = false;
      state.loadError = true;
      const snapshot = state.snapshot;
      state.open =
        !state.dismissed &&
        Boolean(
          snapshot &&
            isReviewStatus(snapshot.status) &&
            snapshot.logisticianId === state.context.userId &&
            !snapshot.special &&
            !snapshot.observer,
        );
    },
    editCard(state, action: PayloadAction<{ carrierId: number; rating?: number; tagIds?: number[]; comment?: string }>) {
      const card = state.cards.find((item) => item.carrierId === action.payload.carrierId);
      if (!card || card.status !== "draft") return;
      Object.assign(card, action.payload);
      card.error = undefined;
    },
    submitReview(state, action: PayloadAction<SubmitReview>) {
      if (state.context?.key !== action.payload.contextKey) return;
      const card = state.cards.find((item) => item.carrierId === action.payload.carrierId);
      if (!card || card.status !== "draft" || card.rating < 1 || card.rating > 5 || card.comment.length > 4000) return;
      if (card.rating <= 3 && !card.comment.trim()) return;
      card.status = "sending";
      card.requestId = action.payload.requestId;
      card.error = undefined;
      state.notice = undefined;
    },
    submitReviewSuccess(state, action: PayloadAction<SubmitReview>) {
      if (state.context?.key !== action.payload.contextKey) return;
      const card = state.cards.find((item) => item.carrierId === action.payload.carrierId);
      if (!card || card.requestId !== action.payload.requestId) return;
      card.status = "submitted";
      card.requestId = undefined;
      state.submittedIds.push(card.carrierId);
    },
    submitReviewReject(state, action: PayloadAction<SubmitReview & { code: string }>) {
      if (state.context?.key !== action.payload.contextKey) return;
      const card = state.cards.find((item) => item.carrierId === action.payload.carrierId);
      if (!card || card.requestId !== action.payload.requestId) return;
      card.status = "draft";
      card.requestId = undefined;
      card.error = action.payload.code;
      state.notice = action.payload.code;
    },
  },
});

export default carrierReviewsSlice;
